<template>
  <div>
    <div class="fixed top-0 left-0 right-0 z-[1024] bg-white shadow-sm">
      <!-- 主导航栏 -->
      <div class="h-[50px] grid grid-cols-[50px_1fr_50px] items-center px-2">
        <div
          class="w-10 h-10 flex items-center justify-center cursor-pointer rounded-lg transition-colors duration-200 text-gray-800 hover:bg-black/5 active:bg-black/10 active:scale-95"
          @click="handleBack"
        >
          <ChevronLeft />
        </div>
        <div class="flex justify-center items-center">
          <h1
            class="text-base font-semibold text-gray-800 m-0 text-center whitespace-nowrap truncate w-[70vw]"
          >
            {{ title }}
          </h1>
        </div>
        <div class="w-10 h-10 flex items-center justify-center">
          <slot name="right"></slot>
        </div>
      </div>

      <!-- Tabs 导航 -->
      <div
        v-if="showTabs && tabs.length > 0"
        class="border-b border-gray-200 bg-white"
      >
        <div class="flex">
          <router-link
            v-for="tab in tabs"
            :key="tab.key"
            :to="{ path: tab.path, query: route.query }"
            :replace="true"
            class="relative py-3 px-2 flex-1 text-center font-medium text-sm no-underline transition-all duration-200 min-w-0 whitespace-nowrap block"
            :class="
              isActiveTab(tab.key)
                ? 'text-primary hover:text-primary'
                : 'text-gray-500 hover:text-gray-700'
            "
          >
            {{ tab.name }}
            <!-- 激活状态的下划线 -->
            <div
              class="absolute bottom-0 left-1/2 transform -translate-x-1/2 h-0.5 bg-primary transition-all duration-300 rounded-full"
              :class="
                isActiveTab(tab.key) ? 'w-[100%] opacity-100' : 'w-0 opacity-0'
              "
            ></div>
          </router-link>
        </div>
      </div>
    </div>
    <div
      :style="'height: ' + (showTabs && tabs.length > 0 ? '98px' : '50px')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ChevronLeft } from 'lucide-vue-next'

interface TabItem {
  path: string
  name: string
  key: string
}

interface Props {
  title?: string
  isMainPage?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  isMainPage: false
})

const router = useRouter()
const route = useRoute()
// 从当前路由的meta中获取tabs信息
const showTabs = computed(() => {
  return route.meta.showTabs
})

const tabs = computed(() => {
  const rawTabs = (route?.meta?.tabs as TabItem[]) || []

  // 如果路径包含参数，需要动态替换
  return rawTabs.map(tab => ({
    ...tab,
    path: replaceRouteParams(tab.path)
  }))
})

// 替换路径中的参数
const replaceRouteParams = (path: string) => {
  let replacedPath = path

  // 替换路径参数
  Object.entries(route.params).forEach(([key, value]) => {
    replacedPath = replacedPath.replace(`:${key}`, value as string)
  })

  return replacedPath
}

const isActiveTab = (tabKey: string) => {
  // 优先使用 meta.activeTab
  if (route.meta?.activeTab) {
    return route.meta.activeTab === tabKey
  }

  // 如果没有设置 activeTab，则根据路径匹配
  const currentTab = tabs.value.find(tab => tab.path === route.path)
  return currentTab?.key === tabKey
}

const handleBack = () => {
  if (props.isMainPage) {
    window.hxe.exit()
  } else {
    router.back()
  }
}
</script>

<style scoped></style>
